import { getCommentReplies } from '@/utils/supabase/queries';
import { CommentWithUser } from './types';
import { SupabaseClient } from '@supabase/supabase-js';

export interface CreateCommentRequest {
  toneId: number;
  parentId?: number | null;
  content: string;
}

export interface EditCommentRequest {
  commentId: number;
  content: string;
}

export interface DeleteCommentRequest {
  commentId: number;
}

export interface VoteCommentRequest {
  commentId: number;
  voteType: 1 | -1 | null; // 1 for upvote, -1 for downvote, null to remove vote
}

export interface VoteCommentResponse {
  success: boolean;
  commentId: number;
  userVote: number | null;
  upvotes: number;
  downvotes: number;
  netVotes: number;
}

/**
 * Create a new comment
 */
export async function createComment(data: CreateCommentRequest): Promise<CommentWithUser> {
  const response = await fetch('/api/comments/add', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to create comment');
  }

  return response.json();
}

/**
 * Edit an existing comment
 */
export async function editComment(data: EditCommentRequest): Promise<CommentWithUser> {
  const response = await fetch('/api/comments/edit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to edit comment');
  }

  return response.json();
}

/**
 * Delete a comment
 */
export async function deleteComment(
  data: DeleteCommentRequest
): Promise<{ success: boolean; id: number }> {
  const response = await fetch('/api/comments/delete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to delete comment');
  }

  return response.json();
}

/**
 * Vote on a comment
 */
export async function voteComment(data: VoteCommentRequest): Promise<VoteCommentResponse> {
  const response = await fetch('/api/comments/vote', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to vote on comment');
  }

  return response.json();
}
